import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from 'src/services/redux/api';
import { USER_FAVORITE_ITEM_TAG_TYPE, userFavoriteListTag } from 'src/services/user/api';
import {
  AddVideoRequest,
  DeleteVideoRequest,
  DeleteVideoThumbnailRequest,
  GetVideoRequest,
  GetVideosRequest,
  PaginationMeta,
  PostCustomLikeVideos,
  PostFeaturedVideo,
  ShareVideoRequest,
  UpdateVideoRequest,
  UploadVideoAttachmentsRequest,
  UploadVideoCaptionRequest,
  UploadVideoRequest,
  UploadVideoThumbnailRequest,
  VideoId,
  VideoItem,
  VideoListRequest,
} from './types';

export const VIDEO_TAG_TYPE = 'Video' as string;
export const videoListTag = { type: VIDEO_TAG_TYPE, id: 'VideoList' };

export const videoApi = createApi({
  baseQuery,
  endpoints: (builder) => ({
    addVideo: builder.mutation<VideoId, AddVideoRequest>({
      invalidatesTags: [videoListTag],
      transformResponse: (res: { data: VideoId }) => res.data,
      query: (body) => ({
        body,
        method: 'POST',
        url: 'admin/video',
      }),
    }),
    deleteVideo: builder.mutation<void, DeleteVideoRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (args) => ({
        body: args,
        method: 'DELETE',
        url: 'admin/video',
      }),
    }),
    deleteVideoFile: builder.mutation<void, DeleteVideoRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (args) => ({
        body: args,
        method: 'DELETE',
        url: 'admin/video/file',
      }),
    }),
    getVideo: builder.query<VideoItem, GetVideoRequest>({
      keepUnusedDataFor: 1,
      transformResponse: (res: { data: VideoItem }) => res?.data,
      query: ({ id }) => ({
        method: 'GET',
        url: `video/${id}`,
      }),
    }),
    getVideos: builder.query<
      { items: VideoItem[]; meta: PaginationMeta },
      GetVideosRequest
    >({
      keepUnusedDataFor: 5,
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ id }) => ({
                type: VIDEO_TAG_TYPE,
                id,
              })),
              videoListTag,
            ]
          : [videoListTag],
      transformResponse: (res: {
        data: { items: VideoItem[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params,
        url: '/admin/video',
      }),
    }),
    getRecommendedVideos: builder.query<
      { items: VideoItem[]; meta: PaginationMeta },
      GetVideosRequest
    >({
      keepUnusedDataFor: 5,
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ id }) => ({
                type: VIDEO_TAG_TYPE,
                id,
              })),
              videoListTag,
            ]
          : [videoListTag],
      transformResponse: (res: {
        data: { items: VideoItem[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params,
        url: '/video/recommended',
      }),
    }),
    shareVideo: builder.mutation<{ message: string }, ShareVideoRequest>({
      query: (args) => ({
        body: args,
        method: 'POST',
        url: `video/share`,
      }),
    }),
    toggleVideoFavorite: builder.mutation<
      { isFavourite: boolean },
      { id: number }
    >({
      invalidatesTags: (res, err, { id }) => [
        userFavoriteListTag,
        { id, type: USER_FAVORITE_ITEM_TAG_TYPE },
        videoListTag,
        { id, type: VIDEO_TAG_TYPE },
      ],
      transformResponse: (res: { data: { isFavourite: boolean } }) => ({
        isFavourite: !!res.data.isFavourite,
      }),
      query: ({ id }) => ({
        method: 'POST',
        url: `video/${id}/toggle-favourite`,
      }),
    }),
    updateVideo: builder.mutation<void, UpdateVideoRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'admin/video',
      }),
    }),
    uploadVideo: builder.mutation<void, UploadVideoRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (body) => ({
        body,
        method: 'POST',
        url: 'admin/video/upload',
      }),
    }),
    uploadVideoCaptions: builder.mutation<void, UploadVideoCaptionRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (props) => {
        const { caption, id } = props;
        return {
          body: caption,
          method: 'POST',
          url: `admin/video/${id}/upload/caption`,
        };
      },
    }),
    uploadVideoThumbnail: builder.mutation<void, UploadVideoThumbnailRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (props) => {
        const { cover, id } = props;
        return {
          body: cover,
          method: 'POST',
          url: `admin/video/${id}/upload/cover`,
        };
      },
    }),
    deleteVideoThumbnail: builder.mutation<void, DeleteVideoThumbnailRequest>({
      invalidatesTags: (res, err, { id }) => [
        videoListTag,
        { type: VIDEO_TAG_TYPE, id },
      ],
      query: (body) => {
        const { id } = body;
        return {
          body: body,
          method: 'DELETE',
          url: `admin/video/${id}/cover`,
        };
      },
    }),
    uploadVideoAttachments: builder.mutation<
      void,
      UploadVideoAttachmentsRequest
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: 'admin/video/upload/attachments',
      }),
    }),
    videoList: builder.query<
      { items: VideoItem[]; meta: PaginationMeta },
      VideoListRequest
    >({
      keepUnusedDataFor: 1,
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ id }) => ({
                type: VIDEO_TAG_TYPE,
                id,
              })),
              videoListTag,
            ]
          : [videoListTag],
      transformResponse: (res: {
        data: { items: VideoItem[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params) => ({
        method: 'GET',
        url: `video`,
        ...(params ? { params } : {}),
      }),
    }),
    addFeaturedVideo: builder.mutation<void, PostFeaturedVideo>({
      query: (body) => ({
        body,
        method: 'POST',
        url: 'admin/video/change-top',
      }),
    }),
    getFeaturedVideo: builder.query<VideoItem, void>({
      transformResponse: (res: { data: VideoItem }) => res?.data,
      query: () => ({
        method: 'GET',
        url: 'video/featured',
      }),
    }),
    setCustomLikeVideos: builder.mutation<void, PostCustomLikeVideos>({
      query: (body) => {
        return {
          body,
          method: 'POST',
          url: 'admin/video/set-custom-ids?autoRecommended',
        };
      },
    }),
    postVideoView: builder.mutation<void, { id: VideoId; secondsWatched: number }>({
      query: ({ id, secondsWatched }) => ({
        method: 'POST',
        url: `video/${id}/view`,
        body: { secondsWatched },
      }),
    }),
    whatsNewVideoList: builder.query<VideoItem[], void>({
      transformResponse: (res: { data: VideoItem[] }) => res?.data || [],
      query: () => ({
        method: 'GET',
        url: `video/whats-new`,
      }),
    }),
  }),
  reducerPath: 'videoApi',
  tagTypes: [VIDEO_TAG_TYPE, USER_FAVORITE_ITEM_TAG_TYPE],
});

export const {
  useAddFeaturedVideoMutation,
  useAddVideoMutation,
  useDeleteVideoFileMutation,
  useDeleteVideoMutation,
  useGetFeaturedVideoQuery,
  useLazyGetFeaturedVideoQuery,
  useGetVideoQuery,
  useGetVideosQuery,
  useLazyGetVideoQuery,
  useLazyGetRecommendedVideosQuery,
  useLazyGetVideosQuery,
  useShareVideoMutation,
  useToggleVideoFavoriteMutation,
  useUpdateVideoMutation,
  useUploadVideoCaptionsMutation,
  useUploadVideoThumbnailMutation,
  useDeleteVideoThumbnailMutation,
  useUploadVideoAttachmentsMutation,
  useUploadVideoMutation,
  useVideoListQuery,
  useSetCustomLikeVideosMutation,
  usePostVideoViewMutation,
  useWhatsNewVideoListQuery,
  useLazyWhatsNewVideoListQuery,
  useLazyVideoListQuery,
} = videoApi;
